import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ApiToken,
  type ListApiTokensResult,
  type McpSurfaceResult,
  type SdkQuickstartResult,
} from '@whinerp/client';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  InlineAction,
  Panel,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { formatCount, formatRelativeDate } from '@whinerp/contracts/format';
import { GRAPHQL_MAX_COST, GRAPHQL_MAX_DEPTH } from '@whinerp/contracts/graphql';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * Setup > Developer (F-124).
 *
 * The screen exists for one moment: the one after a token is issued, when the
 * secret is on screen and will never be on screen again. Everything else here
 * is a list.
 *
 * So the secret is shown in a panel that has to be dismissed deliberately,
 * with the sentence saying plainly that it cannot be recovered. A product that
 * shows a credential in a toast has handed somebody a credential they will
 * lose, and the support ticket that follows cannot be answered.
 */
export function DeveloperPanel(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const errorMessage = useErrorMessage();
  const client = useQueryClient();

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ReadonlySet<string>>(new Set(['records.read']));
  const [secret, setSecret] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<ApiToken | null>(null);
  const [surfaceOf, setSurfaceOf] = useState<string | null>(null);
  const [query, setQuery] = useState(
    '{\n  contacts(limit: 5) {\n    total\n    records { id first_name account { name } }\n  }\n}',
  );

  const sdks = useQuery<SdkQuickstartResult, ApiError>({
    queryKey: ['sdk-quickstart'],
    queryFn: () => api.sdkQuickstart({}),
  });

  const tokens = useQuery<ListApiTokensResult, ApiError>({
    queryKey: ['api-tokens'],
    queryFn: () => api.listApiTokens({}),
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['api-tokens'] });
  };

  const create = useMutation({
    mutationFn: () =>
      api.createApiToken({ body: { name, scopes: [...scopes] } }),
    onSuccess: (result) => {
      // Held in state, not in the list: the list is refetched from the server
      // and the server does not have it.
      setSecret(result.secret);
      setName('');
      refresh();
    },
  });

  /*
   * The playground (F-125).
   *
   * It sits under the tokens rather than on a screen of its own, because the
   * question "what can this API do" and the question "what may this token do"
   * are asked by the same person in the same sitting.
   */
  const run = useMutation({
    mutationFn: () => api.graphqlQuery({ body: { query } }),
  });

  /*
   * What an agent sees with one token (F-128).
   *
   * Fetched from the server rather than worked out here from the token's
   * scopes. A screen that computed its own answer would be a preview that can
   * disagree with the thing it previews - and this preview exists precisely
   * so a security reviewer can trust it.
   */
  const surface = useQuery<McpSurfaceResult, ApiError>({
    queryKey: ['mcp-surface', surfaceOf],
    queryFn: () => api.mcpSurface({ path: { tokenId: surfaceOf! } }),
    enabled: surfaceOf !== null,
  });

  const revoke = useMutation({
    mutationFn: () => api.revokeApiToken({ path: { tokenId: revoking!.tokenId } }),
    onSuccess: () => {
      setRevoking(null);
      refresh();
    },
  });

  // A skeleton means "still loading". Rendering one on error is a spinner that
  // never resolves.
  if (tokens.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (tokens.isError) return <></>;

  const toggle = (scope: string): void =>
    setScopes((held) => {
      const next = new Set(held);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });

  return (
    <Panel data-region="developer" id="developer">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('developer.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('developer.body')}</p>

        {/*
          The secret, once.

          Rendered before the form and before the list, because it is the only
          thing on this screen that cannot be got back by reloading.
        */}
        {secret ? (
          <div
            data-token-secret
            role="alert"
            className="flex flex-col gap-2 rounded-card border border-warning bg-warning-tint p-3"
          >
            <p className="text-body text-ink">{t('developer.secretTitle')}</p>
            <code
              data-token-secret-value
              className="select-all break-all rounded-chip bg-surface px-2 py-1 text-dense text-ink"
            >
              {secret}
            </code>
            <p className="text-meta text-ink-meta">{t('developer.secretBody')}</p>
            <div>
              <Button data-token-secret-done intent="secondary" onClick={() => setSecret(null)}>
                {t('developer.done')}
              </Button>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <Field
            label={t('developer.name')}
            value={name}
            data-token-name
            onChange={(event) => setName(event.target.value)}
          />
          <fieldset className="flex flex-col gap-1">
            <legend className="text-meta text-ink-meta">{t('developer.scopes')}</legend>
            {tokens.data.scopes.map((scope) => (
              <label key={scope.key} className="flex min-h-touch items-center gap-2 text-body text-ink">
                <input
                  type="checkbox"
                  data-token-scope={scope.key}
                  checked={scopes.has(scope.key)}
                  onChange={() => toggle(scope.key)}
                />
                {scope.label}
              </label>
            ))}
          </fieldset>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              data-token-create
              intent="primary"
              disabled={name.trim() === '' || scopes.size === 0 || create.isPending}
              onClick={() => create.mutate()}
            >
              {t('developer.create')}
            </Button>
            <InlineAction
              data-token-contract
              onClick={() => window.open('/openapi.json', '_blank', 'noopener')}
            >
              {t('developer.contract')}
            </InlineAction>
          </div>
          {create.isError ? (
            <p role="alert" className="text-meta text-danger">
              {errorMessage(create.error)}
            </p>
          ) : null}
        </div>

        {tokens.data.tokens.length === 0 ? (
          <EmptyState
            data-token-empty
            illustration="Key"
            title={t('developer.title')}
            body={t('developer.empty')}
            primary={{ label: t('developer.create'), icon: 'Plus', href: '/setup#developer' }}
          />
        ) : (
          <ul className="flex flex-col gap-2" data-token-list>
            {tokens.data.tokens.map((token) => (
              <li
                key={token.tokenId}
                data-token={token.tokenId}
                data-token-revoked={token.revokedAt !== null}
                className="flex flex-wrap items-center gap-2 rounded-chip bg-sunken px-3 py-2"
              >
                <span className="text-body text-ink">{token.name}</span>
                {/* The prefix, so a token in a log line or an environment
                    variable can be matched to this row rather than guessed at. */}
                <code data-token-prefix className="text-dense text-ink-meta">
                  {token.prefix}…
                </code>
                <span className="text-meta text-ink-meta">
                  {t('developer.actsAs', { values: { name: token.actsAsName } })}
                </span>
                <span className="ms-auto text-meta text-ink-meta" data-token-used>
                  {/* Never used and used a long time ago are different facts,
                      and the one deciding what to revoke needs the first said. */}
                  {token.lastUsedAt === null
                    ? t('developer.neverUsed')
                    : t('developer.lastUsed', {
                        values: { when: formatRelativeDate(token.lastUsedAt, format) },
                      })}
                </span>
                {token.revokedAt === null ? (
                  <>
                    <InlineAction
                      data-mcp-show={token.tokenId}
                      onClick={() => setSurfaceOf(token.tokenId)}
                    >
                      {t('mcp.choose')}
                    </InlineAction>
                    <InlineAction
                      data-token-revoke={token.tokenId}
                      onClick={() => setRevoking(token)}
                    >
                      {t('developer.revoke')}
                    </InlineAction>
                  </>
                ) : (
                  <span className="text-meta text-ink-meta">{t('developer.revoked')}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/*
        The quickstarts (F-135).

        Snippets rather than a link to a README, because the question somebody
        has on this screen is "how do I call this with the token I just made",
        and the answer is four lines.

        They come from the SERVER, not from this file. The API's base URL is
        not the web origin - different ports in development, usually different
        hosts in production - so a snippet built here from
        `window.location.origin` hands somebody a line that fails on their
        first call.
      */}
      <div className="mt-4 flex flex-col gap-2" data-sdk>
        <SectionLabel>{t('sdk.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('sdk.body')}</p>
        {sdks.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : sdks.isError ? (
          <></>
        ) : (
          <>
            {sdks.data.libraries.map((library) => (
              <div key={library.language} className="flex flex-col gap-1">
                <p className="text-meta text-ink-meta">{library.label}</p>
                <pre
                  data-sdk-quickstart={library.language}
                  className="select-all overflow-auto rounded-chip bg-sunken p-2 text-dense text-ink"
                >
                  {library.quickstart}
                </pre>
              </div>
            ))}
            {/* Said, rather than left for somebody to discover by running pip. */}
            {sdks.data.libraries.every((library) => !library.published) ? (
              <p data-sdk-unpublished className="text-meta text-ink-meta">
                {t('sdk.notPublished')}
              </p>
            ) : null}
          </>
        )}
      </div>

      <div className="mt-4 flex flex-col gap-2" data-mcp>
        <SectionLabel>{t('mcp.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('mcp.body')}</p>
        <code data-mcp-connection className="select-all rounded-chip bg-sunken px-2 py-1 text-dense text-ink">
          POST /mcp · Authorization: Bearer whin_…
        </code>
        {surfaceOf === null ? (
          <p className="text-meta text-ink-meta">{t('mcp.pick')}</p>
        ) : surface.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : surface.isError ? (
          <p role="alert" className="text-meta text-danger">
            {errorMessage(surface.error)}
          </p>
        ) : (
          <div className="flex flex-col gap-2" data-mcp-surface>
            <p className="text-meta text-ink-meta" data-mcp-acts-as>
              {t('mcp.actsAs', { values: { name: surface.data.actsAs } })}
            </p>
            <SectionLabel>{t('mcp.resources')}</SectionLabel>
            <ul className="flex flex-col" data-mcp-resources>
              {surface.data.resources.map((resource) => (
                <li key={resource.uri} data-mcp-resource={resource.objectKey} className="text-meta text-ink">
                  {resource.name} <code className="text-ink-meta">{resource.uri}</code>
                </li>
              ))}
            </ul>
            <SectionLabel>{t('mcp.tools')}</SectionLabel>
            <ul className="flex flex-col" data-mcp-tools>
              {surface.data.tools.map((tool) => (
                <li key={tool.name} data-mcp-tool={tool.name} className="text-meta text-ink">
                  {tool.name}{' '}
                  <span className="text-ink-meta">
                    {tool.autonomy === 'read-only' ? t('mcp.readOnly') : tool.scope}
                  </span>
                </li>
              ))}
            </ul>
            {/*
              The other half of the answer.

              A list of what an agent CAN do leaves a reviewer asking what it
              cannot - and "it is not in the list" does not say whether that is
              a scope, a module or a permission, which are three different
              things to change.
            */}
            <SectionLabel>{t('mcp.withheld')}</SectionLabel>
            <ul className="flex flex-col" data-mcp-withheld>
              {surface.data.withheld.map((entry) => (
                <li key={`${entry.name}:${entry.reason}`} className="text-meta text-ink-meta">
                  <span className="text-ink">{entry.name}</span> — {entry.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-col gap-2" data-graphql>
        <SectionLabel>{t('graphql.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">
          {t('graphql.body', {
            values: {
              depth: formatCount(GRAPHQL_MAX_DEPTH, format),
              cost: formatCount(GRAPHQL_MAX_COST, format),
            },
          })}
        </p>
        <p className="text-meta text-ink-meta">{t('graphql.readOnly')}</p>
        <textarea
          data-graphql-query
          aria-label={t('graphql.title')}
          rows={6}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="w-full rounded-chip border border-line bg-surface p-2 font-mono text-dense text-ink"
        />
        <div>
          <Button data-graphql-run intent="secondary" busy={run.isPending} onClick={() => run.mutate()}>
            {t('graphql.run')}
          </Button>
        </div>
        {/*
          The refusal, in the same place as the result.
          A cost limit that reports somewhere else is a limit somebody meets
          without seeing why.
        */}
        {run.isError ? (
          <p data-graphql-error role="alert" className="text-meta text-danger">
            {errorMessage(run.error)}
          </p>
        ) : null}
        {run.data ? (
          <pre
            data-graphql-result
            className="max-h-block-lg overflow-auto rounded-chip bg-sunken p-2 text-dense text-ink"
          >
            {run.data.errors.length > 0
              ? run.data.errors.map((problem) => problem.message).join('\n')
              : (run.data.data ?? '')}
          </pre>
        ) : null}
      </div>

      <ConfirmDialog
        open={revoking !== null}
        title={revoking ? t('developer.confirmRevoke', { values: { name: revoking.name } }) : ''}
        confirmLabel={t('developer.revoke')}
        confirmIntent="danger"
        labels={{ cancel: t('action.cancel') }}
        busy={revoke.isPending}
        blockedReason={revoke.isError ? errorMessage(revoke.error) : undefined}
        onCancel={() => setRevoking(null)}
        onConfirm={() => revoke.mutate()}
        data-token-revoke-dialog
      >
        <p className="text-body text-ink-meta">{t('developer.confirmRevokeBody')}</p>
      </ConfirmDialog>
    </Panel>
  );
}
